import { useEffect, useMemo, useRef, useState } from "react";
import { ChevronDown, ClipboardPaste, X as XIcon } from "lucide-react";
import { Bandera } from "@/components/Bandera";

interface Pais {
  id: string;
  codigo: string;
  nombre: string;
}

/**
 * Clave de comparación insensible a mayúsculas y acentos: «panama», «Panamá» y
 * «PANAMÁ» colapsan al mismo texto. NFD separa la letra de su diacrítico y el
 * rango ̀-ͯ borra el diacrítico suelto.
 */
const normalizar = (s: string) =>
  s.normalize("NFD").replace(/[\u0300-\u036f]/g, "").toLowerCase();

/** Separadores admitidos al pegar: coma, punto y coma, espacios y saltos de línea. */
const trocear = (texto: string) =>
  texto.split(/[,;\s]+/).map((t) => t.trim()).filter(Boolean);

interface ResultadoPegado {
  agregados: number;
  yaEstaban: string[];
  noEncontradas: string[];
  malFormadas: string[];
}

/** Redacta el resultado del pegado en una sola frase legible. */
function redactarResultado(r: ResultadoPegado): string {
  const partes: string[] = [];
  partes.push(
    r.agregados === 0
      ? "No se agregó ningún país."
      : `Se agregaron correctamente ${r.agregados} ${r.agregados === 1 ? "país" : "países"}.`,
  );
  if (r.yaEstaban.length) {
    partes.push(
      `${r.yaEstaban.length === 1 ? "La clave" : "Las claves"} ${r.yaEstaban.join(", ")} ya ${r.yaEstaban.length === 1 ? "estaba seleccionada" : "estaban seleccionadas"}.`,
    );
  }
  if (r.noEncontradas.length) {
    partes.push(
      `No se ${r.noEncontradas.length === 1 ? "encontró la siguiente clave" : "encontraron las siguientes claves"} en el catálogo de países: ${r.noEncontradas.join(", ")}. Verifique la información e intente nuevamente.`,
    );
  }
  if (r.malFormadas.length) {
    partes.push(
      `${r.malFormadas.length === 1 ? "La clave" : "Las claves"} ${r.malFormadas.join(", ")} no ${r.malFormadas.length === 1 ? "es válida" : "son válidas"}. Utilice la nomenclatura de tres caracteres correspondiente.`,
    );
  }
  return partes.join(" ");
}

/**
 * Selector multi-país compacto:
 *  - Chips de seleccionados + input en una sola caja.
 *  - Dropdown solo aparece al focus / click; se cierra al click-outside o ESC.
 *  - Búsqueda por nombre o clave, ignorando mayúsculas y acentos.
 *  - Captura masiva pegando claves alpha-3 separadas por coma, punto y coma,
 *    espacios o saltos de línea (RF-002).
 *  - Banderas SVG vía mapping ISO 3166-1 alpha-3 → alpha-2.
 */
export function PaisesPicker({
  label,
  opciones,
  seleccion,
  onChange,
  required,
  disabled,
}: {
  label: string;
  opciones: Pais[];
  seleccion: string[];
  onChange: (ids: string[]) => void;
  required?: boolean;
  disabled?: boolean;
}) {
  const [filtro, setFiltro] = useState("");
  const [open, setOpen] = useState(false);
  const [pegadoAbierto, setPegadoAbierto] = useState(false);
  const [textoPegado, setTextoPegado] = useState("");
  const [resultado, setResultado] = useState<ResultadoPegado | null>(null);
  const containerRef = useRef<HTMLDivElement>(null);
  const inputRef = useRef<HTMLInputElement>(null);
  const textareaRef = useRef<HTMLTextAreaElement>(null);

  useEffect(() => {
    if (!open) return;
    const onDocClick = (e: MouseEvent) => {
      if (containerRef.current && !containerRef.current.contains(e.target as Node)) {
        setOpen(false);
        setFiltro("");
      }
    };
    const onKey = (e: KeyboardEvent) => {
      if (e.key === "Escape") {
        setOpen(false);
        setFiltro("");
        inputRef.current?.blur();
      }
    };
    document.addEventListener("mousedown", onDocClick);
    document.addEventListener("keydown", onKey);
    return () => {
      document.removeEventListener("mousedown", onDocClick);
      document.removeEventListener("keydown", onKey);
    };
  }, [open]);

  const toggle = (id: string) =>
    onChange(seleccion.includes(id) ? seleccion.filter((s) => s !== id) : [...seleccion, id]);

  // Índice por clave normalizada; el catálogo completo son 249 entradas y este
  // mapa se reusa en cada pegado.
  const porCodigo = useMemo(() => {
    const m = new Map<string, Pais>();
    for (const p of opciones) m.set(p.codigo.toUpperCase(), p);
    return m;
  }, [opciones]);

  /**
   * Procesa el texto pegado. No hay conversión automática de alpha-2 a alpha-3
   * («MX» nunca se interpreta como «MEX»): el requerimiento lo prohíbe para
   * evitar sustituciones incorrectas.
   */
  const procesarPegado = (texto: string) => {
    const tokens = trocear(texto);
    const r: ResultadoPegado = {
      agregados: 0,
      yaEstaban: [],
      noEncontradas: [],
      malFormadas: [],
    };
    const nuevos: string[] = [];
    const vistos = new Set<string>();

    for (const token of tokens) {
      const clave = token.toUpperCase();
      // Duplicado dentro del propio pegado: se ignora en silencio, el
      // requerimiento solo pide que el país quede una vez.
      if (vistos.has(clave)) continue;
      vistos.add(clave);

      if (!/^[A-Z]{3}$/.test(clave)) {
        r.malFormadas.push(clave);
        continue;
      }
      const pais = porCodigo.get(clave);
      if (!pais) {
        r.noEncontradas.push(clave);
        continue;
      }
      if (seleccion.includes(pais.id) || nuevos.includes(pais.id)) {
        r.yaEstaban.push(clave);
        continue;
      }
      nuevos.push(pais.id);
      r.agregados += 1;
    }

    if (nuevos.length) onChange([...seleccion, ...nuevos]);
    setResultado(r);
    return r;
  };

  /** Procesa desde la caja de captura múltiple. */
  const procesarDesdeCaja = () => {
    const r = procesarPegado(textoPegado);
    // Solo se limpia si todo entró; si hubo problemas el usuario conserva el
    // texto para corregirlo.
    if (!r.noEncontradas.length && !r.malFormadas.length) setTextoPegado("");
  };

  /**
   * Procesa desde el campo de búsqueda. El usuario que pega «MEX, USA, CHN» en
   * la caja de búsqueda espera que se agreguen los tres, no que se busque la
   * cadena completa; se abre la caja de captura para que el mensaje de
   * resultado quede visible y se descubra dónde vive la función.
   */
  const procesarDesdeBusqueda = (texto: string) => {
    procesarPegado(texto);
    setFiltro("");
    setOpen(false);
    setPegadoAbierto(true);
  };

  /** Un texto es captura múltiple si trae más de una palabra suelta. */
  const esCapturaMultiple = (texto: string) => trocear(texto).length > 1;

  const seleccionados = opciones.filter((p) => seleccion.includes(p.id));
  const filtroNorm = normalizar(filtro.trim());
  const lista = filtroNorm
    ? opciones.filter(
        (p) =>
          normalizar(p.nombre).includes(filtroNorm) ||
          normalizar(p.codigo).includes(filtroNorm),
      )
    : opciones;
  // Ordenamiento garantizado: no-seleccionados primero, alfabéticos.
  const noSel = lista.filter((p) => !seleccion.includes(p.id))
    .sort((a, b) => a.nombre.localeCompare(b.nombre, "es"));

  const hayObservaciones =
    !!resultado && (resultado.noEncontradas.length > 0 || resultado.malFormadas.length > 0);

  return (
    <div>
      <label className="mb-1 block text-xs font-semibold uppercase tracking-wider text-fg-subtle">
        {label}
      </label>
      <div ref={containerRef} className="relative">
        <div
          className={`flex flex-wrap items-center gap-1 rounded border px-2 py-1.5 text-sm ${
            disabled
              ? "pointer-events-none border-edge bg-surface-2 opacity-70"
              : open
                ? "border-primary-strong bg-surface ring-1 ring-primary/30"
                : "border-edge-strong bg-surface"
          }`}
          onClick={() => {
            if (disabled) return;
            setOpen(true);
            inputRef.current?.focus();
          }}
        >
          {seleccionados.map((p) => (
            <span
              key={p.id}
              className="inline-flex items-center gap-1 rounded-full bg-primary-tint px-2 py-0.5 text-xs text-primary-strong"
            >
              <Bandera codigo={p.codigo} />
              <span className="font-mono">{p.codigo}</span>
              <span>{p.nombre}</span>
              <button
                type="button"
                onClick={(e) => {
                  e.stopPropagation();
                  toggle(p.id);
                }}
                className="ml-0.5 rounded-full p-0.5 hover:bg-primary/20"
                aria-label={`Quitar ${p.nombre}`}
              >
                <XIcon className="h-3 w-3" />
              </button>
            </span>
          ))}
          <input
            ref={inputRef}
            type="text"
            value={filtro}
            disabled={disabled}
            onChange={(e) => {
              setFiltro(e.target.value);
              setOpen(true);
            }}
            onPaste={(e) => {
              const texto = e.clipboardData.getData("text");
              if (!esCapturaMultiple(texto)) return; // una sola palabra: filtra normal
              e.preventDefault();
              procesarDesdeBusqueda(texto);
            }}
            onKeyDown={(e) => {
              // Enter sobre varias claves tecleadas a mano: mismo camino.
              if (e.key === "Enter" && esCapturaMultiple(filtro)) {
                e.preventDefault();
                procesarDesdeBusqueda(filtro);
              }
            }}
            onFocus={() => setOpen(true)}
            placeholder={
              seleccionados.length === 0
                ? "Busca o pega varias claves (MEX, USA, CHN…)"
                : "Agregar otro…"
            }
            className="min-w-[120px] flex-1 border-0 bg-transparent text-sm focus:outline-none"
          />
          <ChevronDown
            className={`h-4 w-4 shrink-0 text-fg-faint transition ${open ? "rotate-180" : ""}`}
          />
        </div>

        {/* Atajos rápidos */}
        <div className="mt-1 flex flex-wrap items-center gap-2 text-xs">
          {seleccionados.length > 0 && (
            <span className="text-fg-subtle">
              {seleccionados.length} país{seleccionados.length === 1 ? "" : "es"} seleccionado
              {seleccionados.length === 1 ? "" : "s"}
            </span>
          )}
          {required && seleccionados.length === 0 && (
            <span className="text-estado-warn">Obligatorio</span>
          )}
          {!disabled && (
            <button
              type="button"
              onClick={() => {
                setPegadoAbierto((v) => !v);
                setResultado(null);
                if (!pegadoAbierto) setTimeout(() => textareaRef.current?.focus(), 0);
              }}
              className="inline-flex items-center gap-1 text-primary-strong hover:underline"
            >
              <ClipboardPaste className="h-3 w-3" />
              {pegadoAbierto ? "Cerrar captura múltiple" : "Pegar varias claves"}
            </button>
          )}
        </div>

        {pegadoAbierto && !disabled && (
          <div className="mt-2 rounded-md border border-edge bg-surface-2 p-2">
            <p className="mb-1 text-xs text-fg-subtle">
              Pega las claves de tres caracteres separadas por comas, punto y coma,
              espacios o saltos de línea. Ejemplo:{" "}
              <span className="font-mono">MEX, USA, CHN, KOR, PAN</span>
            </p>
            <textarea
              ref={textareaRef}
              value={textoPegado}
              onChange={(e) => setTextoPegado(e.target.value)}
              onKeyDown={(e) => {
                // Ctrl/Cmd+Enter procesa sin levantar la mano del teclado.
                if (e.key === "Enter" && (e.ctrlKey || e.metaKey)) {
                  e.preventDefault();
                  procesarDesdeCaja();
                }
              }}
              rows={3}
              placeholder="MEX, USA, CHN…"
              className="w-full rounded border border-edge-strong bg-surface px-2 py-1 font-mono text-sm focus:border-primary-strong focus:outline-none"
            />
            <div className="mt-1 flex items-center gap-2">
              <button
                type="button"
                onClick={procesarDesdeCaja}
                disabled={!textoPegado.trim()}
                className="rounded bg-primary px-3 py-1 text-xs font-semibold text-on-primary disabled:opacity-50"
              >
                Agregar países
              </button>
              <button
                type="button"
                onClick={() => {
                  setTextoPegado("");
                  setResultado(null);
                }}
                className="rounded border border-edge-strong px-3 py-1 text-xs"
              >
                Limpiar
              </button>
            </div>
            {resultado && (
              <p
                role="status"
                className={`mt-2 text-xs ${hayObservaciones ? "text-estado-warn" : "text-estado-ok"}`}
              >
                {redactarResultado(resultado)}
              </p>
            )}
          </div>
        )}

        {open && (
          <div className="absolute left-0 right-0 z-20 mt-1 max-h-64 overflow-y-auto rounded-md border border-edge bg-surface shadow-overlay">
            {noSel.length === 0 ? (
              <div className="px-3 py-4 text-center text-xs text-fg-subtle">
                {filtro ? (
                  esCapturaMultiple(filtro) ? (
                    <>
                      <p>Parece que escribiste varias claves.</p>
                      <button
                        type="button"
                        onClick={() => procesarDesdeBusqueda(filtro)}
                        className="mt-1 font-semibold text-primary-strong hover:underline"
                      >
                        Agregar {trocear(filtro).length} claves de una vez
                      </button>
                    </>
                  ) : (
                    `Ningún país coincide con "${filtro}".`
                  )
                ) : (
                  "Todos los países disponibles ya están seleccionados."
                )}
              </div>
            ) : (
              <ul className="py-1">
                {noSel.map((p) => (
                  <li key={p.id}>
                    <button
                      type="button"
                      onClick={() => {
                        toggle(p.id);
                        setFiltro("");
                        inputRef.current?.focus();
                      }}
                      className="flex w-full items-center gap-2 px-3 py-1.5 text-left text-sm hover:bg-surface-2"
                    >
                      <Bandera codigo={p.codigo} />
                      <span className="font-mono text-xs text-fg-subtle">{p.codigo}</span>
                      <span className="text-fg">{p.nombre}</span>
                    </button>
                  </li>
                ))}
              </ul>
            )}
          </div>
        )}
      </div>
    </div>
  );
}
